import { z } from 'zod';

/**
 * The Optiland engine's saved optimization targets ("merit function"): one
 * shared, versioned document per conversation, read and written by both the
 * agent's localOptimize / globalOptimize and the Optiland Optimization tab —
 * the Optiland twin of the Zemax merit function (insight_zemax_merit_function).
 */

/** One target, exactly the entry the agent's optimize tools accept
 *  (src/agent/tools/optimizeTargetSchema.ts OptimizeTargetSchema); a test there
 *  pins the two key sets equal. Semantics and units live in that schema's
 *  describes — not restated here. */
export const OptilandTargetSchema = z.object({
  type: z.string().min(1),
  target: z.number().optional(),
  min_val: z.number().optional(),
  max_val: z.number().optional(),
  weight: z.number().optional(),
  field_scope: z.enum(['single_field', 'all_system_fields']).optional(),
  arm: z.number().int().min(0).optional(),
  surface_number: z.number().int().optional(),
  field_x: z.number().optional(),
  field_y: z.number().optional(),
  pupil_x: z.number().optional(),
  pupil_y: z.number().optional(),
  num_rings: z.number().int().optional(),
  seidel_number: z.number().int().min(1).max(5).optional(),
  fan_axis: z.enum(['tangential', 'sagittal']).optional(),
  glass_catalog: z.array(z.string()).optional(),
  wavelength: z.union([z.number(), z.literal('primary'), z.literal('all')]).optional(),
}).strict();
export type OptilandTarget = z.infer<typeof OptilandTargetSchema>;

export const OPTILAND_OPTIMIZE_METHODS = ['local', 'global'] as const;
export const OPTILAND_GLOBAL_ALGORITHMS = ['differential_evolution', 'dual_annealing', 'basin_hopping'] as const;

/** The run limits the analysis service's request models accept
 *  (analysis-service/models/optimization.py LocalOptimizeRequest /
 *  GlobalOptimizeRequest), which the agent tools also cap at. */
export const OPTILAND_MAX_SECONDS = { local: 180, global: 600 } as const;
export const OPTILAND_MIN_SECONDS = { local: 1, global: 10 } as const;
export const OPTILAND_MAX_ITERATIONS = 5000;

/** How a user-started run runs. Saved with the targets so the tab reopens on
 *  the user's last choice; the agent's own runs take their settings from the
 *  tool call, as the Zemax agent's method tools do. `ray_aiming` applies to
 *  local runs, `algorithm` / `max_iterations` to global ones. */
export const OptilandOptimizationSettingsSchema = z.object({
  method: z.enum(OPTILAND_OPTIMIZE_METHODS),
  polychromatic: z.boolean(),
  max_seconds: z.number().min(OPTILAND_MIN_SECONDS.local).max(OPTILAND_MAX_SECONDS.global),
  precision: z.enum(['fast', 'accurate']),
  auto_boundary_constraints: z.boolean(),
  ray_aiming: z.boolean(),
  algorithm: z.enum(OPTILAND_GLOBAL_ALGORITHMS),
  max_iterations: z.number().int().positive().max(OPTILAND_MAX_ITERATIONS),
}).strict().refine(
  (s) => s.max_seconds >= OPTILAND_MIN_SECONDS[s.method] && s.max_seconds <= OPTILAND_MAX_SECONDS[s.method],
  {
    message: `max_seconds must be ${OPTILAND_MIN_SECONDS.local}–${OPTILAND_MAX_SECONDS.local} for a local run, ${OPTILAND_MIN_SECONDS.global}–${OPTILAND_MAX_SECONDS.global} for a global one`,
    path: ['max_seconds'],
  },
);
export type OptilandOptimizationSettings = z.infer<typeof OptilandOptimizationSettingsSchema>;

/** The agent tools' own defaults (localOptimize / globalOptimize), so a fresh
 *  tab and a fresh agent call start from the same run. */
export const DEFAULT_OPTILAND_OPTIMIZATION_SETTINGS: OptilandOptimizationSettings = {
  method: 'local',
  polychromatic: false,
  max_seconds: 30,
  precision: 'fast',
  auto_boundary_constraints: true,
  ray_aiming: false,
  algorithm: 'basin_hopping',
  max_iterations: 200,
};

export const OPTILAND_MERIT_SOURCES = ['agent', 'ui'] as const;
export type OptilandMeritSource = (typeof OPTILAND_MERIT_SOURCES)[number];

export const OptilandMeritRecordSchema = z.object({
  id: z.string(),
  conversation_id: z.string(),
  version: z.number().int(),
  /** Bumped by each in-place save to this version; see if_revision. */
  revision: z.number().int(),
  targets: z.array(OptilandTargetSchema),
  settings: OptilandOptimizationSettingsSchema,
  source: z.enum(OPTILAND_MERIT_SOURCES),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OptilandMeritRecord = z.infer<typeof OptilandMeritRecordSchema>;

/** GET /optiland-merit/current — null for a draft chat or no saved targets. */
export const OptilandMeritCurrentSchema = OptilandMeritRecordSchema.nullable();

export const OptilandMeritSaveRequestSchema = z.object({
  conversation_id: z.string().uuid(),
  analysis_engine: z.literal('optiland'),
  targets: z.array(OptilandTargetSchema),
  settings: OptilandOptimizationSettingsSchema,
  /** Optimistic concurrency: the version the editor loaded (0 = none yet). */
  if_version: z.number().int().min(0).optional(),
  /** The revision of that version the editor's state is based on. A save
   *  that would update the version in place is refused (409) when another
   *  tab has written it since. */
  if_revision: z.number().int().min(1).optional(),
}).strict();

export const OptilandMeritSaveSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  revision: z.number().int(),
  /** Why the saved targets would not run against the current system (empty
   *  when they would). Saved anyway: the user may still be mid-edit. */
  validation_errors: z.array(z.string()),
});

export const OptilandMeritHistorySchema = z.object({
  conversation_id: z.string(),
  current_version: z.number().int().nullable(),
  total_versions: z.number().int(),
  truncated: z.boolean(),
  versions: z.array(z.object({
    id: z.string(),
    version: z.number().int(),
    source: z.enum(OPTILAND_MERIT_SOURCES),
    created_at: z.string(),
    is_active: z.boolean(),
    num_targets: z.number().int(),
    notes: z.string().nullable(),
  })),
});

export const OptilandMeritRestoreRequestSchema = z.object({
  conversation_id: z.string().uuid(),
  version: z.number().int().positive(),
}).strict();

export const OptilandMeritRestoreSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  restored_from: z.number().int(),
});

export const OptilandOptimizeRunRequestSchema = z.object({
  conversation_id: z.string().uuid(),
  analysis_engine: z.literal('optiland'),
}).strict();

/** The run is accepted and executes server-side; its progress and result
 *  arrive on the conversation stream's optimization_* events, the same
 *  events an agent run emits. */
export const OptilandOptimizeRunSchema = z.object({
  accepted: z.literal(true),
});
